import { useMemo, useState } from "react";
import { useLocalSearchParams, useRouter } from "expo-router";
import { StyleSheet, Text, View } from "react-native";

import { UpgradePrompt } from "~/components/composed/UpgradePrompt";
import {
  AppText,
  Button,
  ButtonText,
  Card,
  EmptyState,
  ProtectedScreen,
  SearchableSelect,
  SectionHeader,
  Stack,
  TextField,
} from "~/components/ui";
import { ItemAttachments } from "~/features/items/ItemAttachments";
import {
  CATEGORY_OPTIONS,
  REMINDER_KIND_LABELS,
  STATUS_OPTIONS,
} from "~/features/items/labels";
import { usePlan } from "~/hooks/usePlan";
import {
  itemSpecs,
  useItem,
  useItemActions,
  useItemReminders,
} from "~/state/items";
import { useKits } from "~/state/kits";
import {
  fonts,
  spacing,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";
import type {
  Item,
  ItemAttachment,
  ItemReminder,
} from "~/api/generated/types.gen";
import { BUILT_IN_SPEC_KEYS, BUILT_IN_SPEC_LABELS } from "~/utils/plan-limits";

type ItemCategory = Item["category"];
type ItemStatus = Item["status"];
type ReminderKind = ItemReminder["kind"];

export default function ItemDetailScreen() {
  const stylesheet = useThemedStyles(makeStyles);
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const item = useItem(id);
  const kits = useKits();
  const actions = useItemActions();
  const plan = usePlan();
  const reminders = useItemReminders(item?.id);
  const [customKey, setCustomKey] = useState("");
  const [customValue, setCustomValue] = useState("");
  const [reminderKind, setReminderKind] = useState<ReminderKind>("warranty");
  const [reminderDue, setReminderDue] = useState("");
  const [reminderNotes, setReminderNotes] = useState("");

  const kitOptions = useMemo(
    () => [
      { value: "", label: "Ungrouped" },
      ...kits.map((kit) => ({ value: kit.id, label: kit.name })),
    ],
    [kits],
  );

  if (!item) {
    return (
      <ProtectedScreen title="Item">
        <EmptyState
          title="Item not found"
          description="It may have been deleted on another device."
        />
      </ProtectedScreen>
    );
  }

  const specs = itemSpecs(item);
  const kit = kits.find((candidate) => candidate.id === item.kit_id);

  const setSpec = (key: string, value: string) => {
    const next = { ...specs };
    if (value.trim()) next[key] = value.trim();
    else delete next[key];
    actions.update(item.id, { specs: next });
  };

  return (
    <ProtectedScreen
      title={item.name}
      subtitle={kit?.name ? `In ${kit.name}` : "Ungrouped"}
    >
      <Card gap="md">
        <SectionHeader title="Identity" />
        <View style={stylesheet.grid}>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Name
            </AppText>
            <TextField
              accessibilityLabel="Item name"
              value={item.name}
              onChangeText={(value) => actions.update(item.id, { name: value })}
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Brand
            </AppText>
            <TextField
              accessibilityLabel="Brand"
              value={item.brand ?? ""}
              placeholder="Makita"
              onChangeText={(value) =>
                actions.update(item.id, { brand: value })
              }
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Model
            </AppText>
            <TextField
              accessibilityLabel="Model"
              value={item.model ?? ""}
              onChangeText={(value) =>
                actions.update(item.id, { model: value })
              }
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Serial
            </AppText>
            <TextField
              accessibilityLabel="Serial number"
              value={item.serial_number ?? ""}
              style={stylesheet.mono}
              onChangeText={(value) =>
                actions.update(item.id, { serialNumber: value })
              }
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Category
            </AppText>
            <SearchableSelect
              title="Category"
              accessibilityLabel="Category"
              value={item.category}
              options={CATEGORY_OPTIONS}
              onValueChange={(value) =>
                actions.update(item.id, { category: value as ItemCategory })
              }
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Status
            </AppText>
            <SearchableSelect
              title="Status"
              accessibilityLabel="Status"
              value={item.status}
              options={STATUS_OPTIONS}
              onValueChange={(value) =>
                actions.update(item.id, { status: value as ItemStatus })
              }
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Kit
            </AppText>
            <SearchableSelect
              title="Kit"
              accessibilityLabel="Kit"
              value={item.kit_id ?? ""}
              options={kitOptions}
              onValueChange={(value) =>
                actions.update(item.id, { kitId: value || null })
              }
            />
          </View>
        </View>
      </Card>

      <Card gap="md">
        <SectionHeader
          title="Purchase"
          description="Date, price, and vendor stay on the free plan."
        />
        <View style={stylesheet.grid}>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Purchase date
            </AppText>
            <TextField
              accessibilityLabel="Purchase date"
              value={item.purchase_date ?? ""}
              placeholder="YYYY-MM-DD"
              onChangeText={(value) =>
                actions.update(item.id, { purchaseDate: value || null })
              }
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Price
            </AppText>
            <TextField
              accessibilityLabel="Purchase price"
              value={
                item.purchase_price == null ? "" : String(item.purchase_price)
              }
              placeholder="0.00"
              keyboardType="decimal-pad"
              onChangeText={(value) =>
                actions.update(item.id, {
                  purchasePrice: value.trim() ? Number(value) : null,
                })
              }
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Currency
            </AppText>
            <TextField
              accessibilityLabel="Currency"
              value={item.currency}
              onChangeText={(value) =>
                actions.update(item.id, { currency: value || "NZD" })
              }
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Vendor
            </AppText>
            <TextField
              accessibilityLabel="Vendor"
              value={item.vendor ?? ""}
              placeholder="Bunnings, PB Tech..."
              onChangeText={(value) =>
                actions.update(item.id, { vendor: value })
              }
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Warranty expires
            </AppText>
            <TextField
              accessibilityLabel="Warranty expiry"
              value={item.warranty_expires_at ?? ""}
              placeholder="YYYY-MM-DD"
              onChangeText={(value) =>
                actions.update(item.id, { warrantyExpiresAt: value || null })
              }
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Service interval (months)
            </AppText>
            <TextField
              accessibilityLabel="Service interval months"
              value={
                item.service_interval_months == null
                  ? ""
                  : String(item.service_interval_months)
              }
              keyboardType="number-pad"
              onChangeText={(value) =>
                actions.update(item.id, {
                  serviceIntervalMonths: value.trim()
                    ? Number.parseInt(value, 10)
                    : null,
                })
              }
            />
          </View>
        </View>
        <View style={stylesheet.field}>
          <AppText size="xs" weight="semibold">
            Notes
          </AppText>
          <TextField
            accessibilityLabel="Notes"
            value={item.notes ?? ""}
            multiline
            style={stylesheet.textarea}
            onChangeText={(value) => actions.update(item.id, { notes: value })}
          />
        </View>
      </Card>

      <Card gap="md">
        <SectionHeader
          title="Specs"
          description="Built-in fields are free. Custom keys need Pro."
        />
        <View style={stylesheet.grid}>
          {BUILT_IN_SPEC_KEYS.map((key) => (
            <View key={key} style={stylesheet.field}>
              <AppText size="xs" weight="semibold">
                {BUILT_IN_SPEC_LABELS[key]}
              </AppText>
              <TextField
                accessibilityLabel={BUILT_IN_SPEC_LABELS[key]}
                value={specs[key] ?? ""}
                style={stylesheet.mono}
                placeholder="—"
                onChangeText={(value) => setSpec(key, value)}
              />
            </View>
          ))}
        </View>
        {Object.entries(specs)
          .filter(
            ([key]) => !(BUILT_IN_SPEC_KEYS as readonly string[]).includes(key),
          )
          .map(([key, value]) => (
            <View key={key} style={stylesheet.customRow}>
              <Text style={stylesheet.customKey}>{key}</Text>
              <TextField
                accessibilityLabel={key}
                value={value}
                style={stylesheet.mono}
                onChangeText={(next) => setSpec(key, next)}
              />
              <Button variant="text" compact onPress={() => setSpec(key, "")}>
                <ButtonText>Remove</ButtonText>
              </Button>
            </View>
          ))}
        {plan.canUseCustomSpecs ? (
          <Stack gap="sm">
            <View style={stylesheet.grid}>
              <View style={stylesheet.field}>
                <AppText size="xs" weight="semibold">
                  Custom field
                </AppText>
                <TextField
                  accessibilityLabel="Custom spec key"
                  value={customKey}
                  placeholder="SKU, thread pitch..."
                  onChangeText={setCustomKey}
                />
              </View>
              <View style={stylesheet.field}>
                <AppText size="xs" weight="semibold">
                  Value
                </AppText>
                <TextField
                  accessibilityLabel="Custom spec value"
                  value={customValue}
                  style={stylesheet.mono}
                  onChangeText={setCustomValue}
                />
              </View>
            </View>
            <Button
              align="start"
              variant="outline"
              disabled={!customKey.trim() || !customValue.trim()}
              onPress={() => {
                setSpec(customKey.trim(), customValue.trim());
                setCustomKey("");
                setCustomValue("");
              }}
            >
              <ButtonText>Add custom spec</ButtonText>
            </Button>
          </Stack>
        ) : (
          <UpgradePrompt
            title="Need a custom spec field?"
            reason="GearSync Pro lets you store arbitrary key/value templates — SKU, bit size, firmware, whatever the item needs."
          />
        )}
      </Card>

      <Card gap="md">
        <SectionHeader title="Attachments" />
        <ItemAttachments itemId={item.id} />
      </Card>

      <Card gap="md">
        <SectionHeader
          title="Reminders"
          description="Warranty and service due dates. Pro only."
        />
        {plan.canUseReminders ? (
          <Stack gap="md">
            {reminders.length === 0 ? (
              <EmptyState
                title="No reminders"
                description="Add a warranty or service date so it does not sneak up on you."
              />
            ) : (
              reminders.map((reminder) => (
                <View key={reminder.id} style={stylesheet.reminderRow}>
                  <Stack gap="xs">
                    <AppText weight="semibold">
                      {REMINDER_KIND_LABELS[reminder.kind]}
                    </AppText>
                    <AppText size="xs">
                      Due {reminder.due_on}
                      {reminder.notes ? ` · ${reminder.notes}` : ""}
                    </AppText>
                  </Stack>
                  <Button
                    variant="text"
                    compact
                    onPress={() => actions.removeReminder(reminder.id)}
                  >
                    <ButtonText>Remove</ButtonText>
                  </Button>
                </View>
              ))
            )}
            <View style={stylesheet.grid}>
              <View style={stylesheet.field}>
                <AppText size="xs" weight="semibold">
                  Kind
                </AppText>
                <SearchableSelect
                  title="Reminder kind"
                  accessibilityLabel="Reminder kind"
                  value={reminderKind}
                  options={[
                    { value: "warranty", label: "Warranty" },
                    { value: "service", label: "Service" },
                  ]}
                  onValueChange={(value) =>
                    setReminderKind(value as ReminderKind)
                  }
                />
              </View>
              <View style={stylesheet.field}>
                <AppText size="xs" weight="semibold">
                  Due on
                </AppText>
                <TextField
                  accessibilityLabel="Reminder due date"
                  value={reminderDue}
                  placeholder="YYYY-MM-DD"
                  onChangeText={setReminderDue}
                />
              </View>
            </View>
            <TextField
              accessibilityLabel="Reminder notes"
              value={reminderNotes}
              placeholder="Notes (optional)"
              onChangeText={setReminderNotes}
            />
            <Button
              align="start"
              variant="outline"
              disabled={!reminderDue.trim()}
              onPress={() => {
                actions.addReminder(
                  item.id,
                  reminderKind,
                  reminderDue.trim(),
                  reminderNotes,
                );
                setReminderDue("");
                setReminderNotes("");
              }}
            >
              <ButtonText>Add reminder</ButtonText>
            </Button>
          </Stack>
        ) : (
          <UpgradePrompt
            title="Warranty and service reminders"
            reason="GearSync Pro tracks due dates so a warranty does not expire unnoticed."
          />
        )}
      </Card>

      <Button
        variant="outline"
        onPress={() => {
          actions.remove(item.id);
          router.replace("/items" as never);
        }}
      >
        <ButtonText>Delete item</ButtonText>
      </Button>
    </ProtectedScreen>
  );
}

const makeStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    grid: { flexDirection: "row", flexWrap: "wrap", gap: spacing.m },
    field: { minWidth: 220, flex: 1, gap: spacing.xs },
    textarea: { minHeight: 86, textAlignVertical: "top" },
    mono: { fontFamily: fonts.mono },
    customRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.s,
    },
    customKey: {
      minWidth: 88,
      color: colors.heading,
      fontFamily: fonts.monoBold,
      fontSize: 12,
    },
    reminderRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: spacing.m,
    },
  });
